import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  statesAPI,
  citiesAPI,
  type State,
  type City,
  type CityCreatePayload,
} from "../../../services/api";

function getStateName(city: City, states: State[]) {
  if (city.state_name) {
    return city.state_name;
  }
  const id = city.state;
  if (id === null) {
    return "—";
  }
  const s = states.find(p => String(p.id) === String(id));
  return s?.name ?? String(id);
}

export default function CityPage() {
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<CityCreatePayload>({
    name: "",
    state: "",
    is_active: true,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<City | null>(null);
  const [deleting, setDeleting] = useState<City | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchStates = async () => {
    try {
      const list = await statesAPI.list();
      setStates(Array.isArray(list) ? list : []);
    } catch {
      setStates([]);
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const list = await citiesAPI.list();
      setCities(Array.isArray(list) ? list : []);
    } catch {
      toast.error("Failed to load cities");
      setCities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);
  useEffect(() => {
    fetchList();
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("City name is required");
      return;
    }
    if (!form.state) {
      toast.error("Please select a state");
      return;
    }
    setSubmitLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        state: Number(form.state) || form.state,
        is_active: form.is_active,
      };
      if (editing) {
        await citiesAPI.update(editing.id, payload);
        toast.success("City updated successfully");
      } else {
        await citiesAPI.create(payload);
        toast.success("City created successfully");
      }
      setDialogOpen(false);
      setForm({ name: "", state: "", is_active: true });
      setEditing(null);
      fetchList();
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to save city";
      toast.error(String(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeleteLoading(true);
    try {
      await citiesAPI.delete(deleting.id);
      toast.success("City deleted successfully");
      setDeleting(null);
      fetchList();
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to delete city";
      toast.error(String(msg));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">City</h1>
        <p className="text-muted-foreground">Manage cities</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Cities ({cities.length})</CardTitle>
          <Dialog
            open={dialogOpen}
            onOpenChange={open => {
              setDialogOpen(open);
              if (!open) {
                setForm({ name: "", state: "", is_active: true });
                setEditing(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setForm({
                    name: "",
                    state: states[0]?.id !== null ? String(states[0].id) : "",
                    is_active: true,
                  });
                  setEditing(null);
                  setDialogOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create new
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Edit City" : "Create New City"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>State *</Label>
                  <Select
                    value={form.state}
                    onValueChange={v =>
                      setForm(prev => ({ ...prev, state: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map(s => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="city-name">Name *</Label>
                  <Input
                    id="city-name"
                    value={form.name}
                    onChange={e =>
                      setForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="City name"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="city-active"
                    checked={form.is_active ?? true}
                    onCheckedChange={c =>
                      setForm(prev => ({ ...prev, is_active: c }))
                    }
                  />
                  <Label htmlFor="city-active">Active</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={submitLoading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={submitLoading}>
                    {submitLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : editing ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : cities.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              No cities yet. Create one to get started.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cities.map((c, i) => (
                  <TableRow key={c.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{getStateName(c, states)}</TableCell>
                    <TableCell>
                      {c.is_active !== false ? "Active" : "Inactive"}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditing(c);
                            setForm({
                              name: c.name,
                              state:
                                c.state !== null
                                  ? String(c.state)
                                  : states[0]?.id !== null
                                    ? String(states[0].id)
                                    : "",
                              is_active: c.is_active ?? true,
                            });
                            setDialogOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleting(c)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete City</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{deleting.name}</strong>
                ?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
